import { Button } from 'components/atoms';
import ArtistBandCard from 'components/molecules/card/artist-band/ArtistBandCard';
import ViewHint from 'components/molecules/hint/ViewHint';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import ArtistSelectPopupForm from 'components/molecules/popup/artist-select/ArtistSelectPopupForm';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { IArtistMutationForm, IArtistResponse } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { object, string } from 'yup';

export const artistsInBandSchema = object().shape({
  artistId: string().required(''),
  nickname: string(),
});

const ArtistBand = () => {
  const { values, status, setFieldValue, initialValues }: IFormikContextType<IArtistMutationForm> =
    useFormikContext<IArtistMutationForm>();
  const { t } = useTranslation();

  const { setForm } = useStoreState();

  const navigate = useNavigate();

  const onRemoveArtist = (id: string) =>
    setFieldValue(
      'artistsInBand',
      values.artistsInBand.filter((artist) => artist.artistId !== id),
    );

  const handleNavigate = (domain: string, tab: string) => {
    setForm('artistForm', values);

    const action = values.id ? `edit/${values.id}` : 'create';

    navigate(`/${domain}/${action}/${tab}`);
  };

  const onSelectArtist = (artist: IArtistResponse, actions: PopupActions) => {
    setFieldValue('artistsInBand', [
      ...values.artistsInBand,
      {
        nickname: artist.nickname,
        artistId: artist.id,
        artistStatus: artist.artistStatus,
        previewPicture: artist.profilePictureLink,
      },
    ]);

    actions.close();
  };

  return (
    <StepsTab
      nextName={'external'}
      onNext={() => handleNavigate('artist', 'external')}
      backName={'data'}
      onBack={() => handleNavigate('artist', 'data')}
    >
      <ViewHint hintId="artist-band" />
      {!status?.displayMode && (
        <ArtistSelectPopupForm
          onSelectArtist={onSelectArtist}
          trigger={
            <Button fullWidth variant={BUTTON_VARIANT.white} size="large">
              <Button.Icon icon={<PiUserPlus />} />
              <Button.Text text={t('button.add-artist-to-band')} />
            </Button>
          }
          disabledArtists={[values.id, ...values.artistsInBand.map((a) => a.artistId)]}
          mode="band"
        />
      )}
      <ListWrapper
        isLoading={status?.isLoading}
        items={values.artistsInBand}
        item={(artist, index) => (
          <ArtistBandCard
            key={index}
            artist={artist}
            displayMode={status?.displayMode}
            onRemove={() => onRemoveArtist(artist.artistId)}
          />
        )}
      />
    </StepsTab>
  );
};

export default ArtistBand;
